/**
 * Die **Neigung** — wie schräg ein **Canvas-Element** liegt, und die Rechnung
 * hinter dem Drehen.
 *
 * Bewusst ohne React-Bezug, wie die `utils/` nebenan: Die Rechnung soll ohne
 * gerenderten Canvas prüfbar sein.
 *
 * **Der Winkel ist Darstellung, nicht Geometrie** (ADR 0015): Das Rechteck
 * eines Elements bleibt achsparallel, und mit ihm Einrasten, Hilfslinien,
 * Bereichs-Zugehörigkeit und das Skalieren. Was hier gerechnet wird, ist der
 * Winkel, um den die KARTE gekippt gezeichnet wird — nicht ihre Lage.
 *
 * Siehe „Neigung" in CONTEXT.md und `.claude/tasks/canvas-neigung-drehen.md`.
 */

import { CANVAS_TAPE_ANGLE_MAX, CANVAS_TAPE_ANGLE_STEP } from '../tapes/canvasTapes'


/**
 * Wie weit eine Raste von der nächsten entfernt liegt — **dieselben 7° wie beim
 * Klebestreifen.**
 *
 * Vom Streifen ÜBERNOMMEN und nicht danebengeschrieben: Ein Canvas, auf dem
 * zwei Dinge in verschiedenen Stufen rasten, hätte zwei Sprachen für dieselbe
 * Sache — und eine der beiden Zahlen liefe früher oder später weg.
 */
export const CANVAS_NOTE_ROTATION_STEP = CANVAS_TAPE_ANGLE_STEP

/**
 * Wie weit sich ein Zettel neigen lässt, **nach beiden Seiten** — bis zur
 * steilsten Stufe, die auch der Streifen erreicht (42°, in der Ecke und nur von
 * Hand).
 *
 * Nach beiden Seiten ist der Unterschied zum Streifen: Dessen eine Richtung ist
 * eine Regel der ECKE (andersherum kippte sein freies Ende ins Textfeld). Ein
 * Zettel hat kein freies Ende, er hat vier gleichwertige Ecken.
 *
 * Die 42 sind am Papier nachgerechnet (siehe `CANVAS_TAPE_ANGLES_WIDE`) — und
 * wo es eine begründete Grenze schon gibt, braucht es keine zweite erfundene
 * daneben. Das Raster geht bei 7er-Schritten genau auf: sechs Stufen je Seite,
 * mit der Null in der Mitte.
 */
export const CANVAS_NOTE_ROTATION_MAX = CANVAS_TAPE_ANGLE_MAX

/** Ein Punkt auf dem Schirm. */
export interface CanvasPoint {
  x: number
  y: number
}

/**
 * Die Rasten eines **Zettels** oder **Memos**: −42 … 42 in 7°-Stufen, die Null
 * in der Mitte — dreizehn Stufen.
 *
 * Als Liste und nicht als Rechnung aus Schritt und Grenze: Dieselbe Gestalt wie
 * `canvasTapeAngles`, und `snapToRasten` nimmt beide ohne Fallunterscheidung.
 */
export function canvasNoteRotations(): number[] {
  const rasten: number[] = []
  for (let deg = -CANVAS_NOTE_ROTATION_MAX; deg <= CANVAS_NOTE_ROTATION_MAX; deg += CANVAS_NOTE_ROTATION_STEP) {
    rasten.push(deg)
  }
  return rasten
}

/**
 * Der Winkel vom Mittelpunkt zum Zeiger — Grad, **im Uhrzeigersinn**.
 *
 * Im Uhrzeigersinn, weil der Bildschirm nach unten positiv zählt: `atan2` mit
 * `dy` zuerst liefert damit ohne Vorzeichenwechsel dieselbe Drehrichtung, die
 * CSS `rotate` meint. Wer nach rechts unten fährt, bekommt +45°.
 *
 * **Zoominvariant.** Ein Winkel ändert sich nicht, wenn die Fläche skaliert
 * wird — hier wird deshalb nichts durch den Zoom geteilt. (Die Fangschwellen im
 * Canvas rechnen umgekehrt: Sie meinen Abstände auf der FLÄCHE.)
 */
export function pointerAngle(center: CanvasPoint, point: CanvasPoint): number {
  // Der Mittelpunkt selbst ergibt 0 statt NaN — `atan2(0, 0)` ist 0.
  return (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI
}

/**
 * Eine Winkel-DIFFERENZ auf den kurzen Weg gebracht: `[−180, 180)`.
 *
 * **Das ist der Kern der Geste.** `atan2` springt bei ±180°: Wer den Zeiger von
 * 170° nach −170° führt, ist zehn Grad im Uhrzeigersinn weiter — die rohe
 * Differenz sagte aber −340 und risse den Zettel einmal ganz herum.
 */
export function normalizeAngle(deg: number): number {
  return (((deg + 180) % 360) + 360) % 360 - 180
}

/**
 * Der Winkel, auf die nächste Raste gezogen.
 *
 * **An den Enden ist Schluss, kein Umlauf:** Wer über 42° hinauszieht, soll
 * nicht bei −42° wieder herauskommen — ein Regler, der springt, ist keiner.
 * Das erledigt die Suche nach der nächsten Raste von selbst: Außerhalb der
 * Liste ist das immer ein Ende.
 *
 * Dieselbe Rechnung wie `nearestCanvasTapeAngle` — bei Gleichstand gewinnt die
 * frühere Raste.
 */
export function snapToRasten(angle: number, rasten: number[]): number {
  return rasten.reduce(
    (beste, raste) => (Math.abs(raste - angle) < Math.abs(beste - angle) ? raste : beste),
    rasten[0]!,
  )
}

/**
 * Die Raste, auf der ein Zug endet.
 *
 * Die gefahrene Strecke wird auf den Winkel BEIM ANFASSEN aufgetragen, nicht
 * auf den zuletzt gezeigten: Sonst summierten sich Rundungsfehler über die
 * Frames, und der Zettel driftete unter der ruhig gehaltenen Hand.
 *
 * @param von         der Winkel, mit dem der Zug begann
 * @param angleAtDown wo der Zeiger beim Drücken stand (siehe {@link pointerAngle})
 * @param angleNow    wo er jetzt steht
 * @param rasten      die erlaubten Stufen — beim Zettel {@link canvasNoteRotations},
 *                    beim Streifen `canvasTapeAngles(spot)`
 */
export function rotationAfterDrag(
  von: number,
  angleAtDown: number,
  angleNow: number,
  rasten: number[],
): number {
  return snapToRasten(von + normalizeAngle(angleNow - angleAtDown), rasten)
}

/**
 * Das Umgebungsrechteck eines gedrehten Rechtecks.
 *
 * Gebraucht für den **Überstand**: Wie weit die Ecken des geneigten Papiers über
 * sein achsparalleles Rechteck hinausragen — daran hängt, wie hoch die
 * **Options-Leiste** schweben muss und wann sie unter den Zettel klappt.
 *
 * `Überstand oben = (height − h) / 2`, denn gedreht wird um die Mitte.
 */
export function rotatedBounds(w: number, h: number, deg: number): { width: number; height: number } {
  const rad = (deg * Math.PI) / 180
  const cos = Math.abs(Math.cos(rad))
  const sin = Math.abs(Math.sin(rad))
  return {
    width: w * cos + h * sin,
    height: w * sin + h * cos,
  }
}
